import _ from 'lodash';
import { createContext } from 'react';

import { bindServices } from '@/services/api';
import { updateFavorite } from './service';

const api = bindServices('entities');
import request from '@/utils/infrastructure/communication/request';
import { CoreEnums } from '@/utils/domain/constants';
import { toPaginate } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';
import { filtersConfig } from './ManageEntities/utils';
import { groupsFiltersConfig } from './manage-groups/utils';

export const DynamicEntityContext = createContext({});

const { DynamicEntityOperation } = CoreEnums;
const meta = { page: 1, limit: 5, count: 0, totalPages: 0 };

const initialState = {
  data: [],
  groups: [],
  premium: [],
  favorites: [],
  groupsPagination: meta,
  premiumPagination: meta,
  pagination: meta,
  orderBy: '',
  currentEntity: null,
  searchCriteria: '',
  filtersConfig,
  groupsFiltersConfig,
  favoritesChanged: false,
};

const Model = {
  namespace: 'entities',
  state: initialState,
  effects: {
    *fetchGroups({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'entity/groups',
          ...CoreQueries.ManageEntities.Groups,
          ...payload,
        });
        yield put({
          type: 'queryGroups',
          payload: {
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {}
    },

    *fetchList({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'entity/manage',
          ...CoreQueries.ManageEntities.Entities,
          ...payload,
        });
        yield put({
          type: 'queryEntities',
          payload: {
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {}
    },

    *fetchPremiumList({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'entity/manage',
          ...CoreQueries.ManageEntities.PremiumEntities,
          ...payload,
        });
        yield put({
          type: 'queryPremiumEntities',
          payload: {
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {}
    },

    *fetchFavorites({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'entity/favorites',
          ...CoreQueries.ManageEntities.Favorites,
          ...payload,
        });
        yield put({ type: 'queryFavorites', payload: res });
      } catch (e) {}
    },

    *setFavorite({ payload }, { call, put }) {
      try {
        yield call(updateFavorite, payload);
        yield put({ type: 'querySetFavorite', payload });

        yield put({ type: 'markFavoritesChanged' });
      } catch (e) {}
    },

    *saveEntity({ payload }, { call, put }) {
      try {
        yield call(api.create, { route: 'dynamicEntity', ...payload });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'querySaveEntity' });
      } catch (err) {
        if (err?.message) {
          const { message, validationErrors } = err;
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message },
            validationErrors,
          });
        }
      }
    },

    *entityGroup({ payload }, { call, put }) {
      try {
        yield call(api.create, { route: 'dynamicEntityGroup', data: payload });
        yield put({
          type: 'queryEntityGroup',
          payload: DynamicEntityOperation.Add,
        });
      } catch (err) {
        if (err?.message) {
          const { message, validationErrors } = err;
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message },
            validationErrors,
          });
        }
      }
    },

    *search({ payload }, { put }) {
      yield put({ type: 'querySearch', payload });
    },

    *onTabChange({ payload }, { put, select }) {
      if (payload === 'favorites') {
        const { favoritesChanged } = yield select((state) => state.entities);
        if (favoritesChanged) {
          yield put({ type: 'fetchFavorites', payload: { page: 1 } });
          yield put({ type: 'clearFavoritesChanged' });
        }
      }
    },

    *toggleActive({ payload }, { call, put }) {
      try {
        yield call(request.post, `/dynamicEntity/activate/${payload.id}/${payload.isActive}`, {});
        yield put({
          type: 'queryToggleActive',
          payload: { id: payload.id, isActive: payload.isActive },
        });
      } catch (e) {}
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'orderBy', payload });
    },

    *hideAllForms(o, { put }) {
      yield put({ type: 'hideForms' });
    },

    *cleanup(o, { put }) {
      yield put({ type: 'queryCleanup' });
    },
  },
  reducers: {
    queryGroups(state, { payload }) {
      return {
        ...state,
        groups: payload?.items || [],
        executedCommand: null,
        viewGroupsExecutedCommand: null,
        groupsPagination: {
          ...state.groupsPagination,
          ...toPaginate(payload),
        },
      };
    },

    queryEntities(state, { payload }) {
      return {
        ...state,
        data: payload?.items || [],
        executedCommand: null,
        pagination: {
          ...state.pagination,
          ...toPaginate(payload),
        },
      };
    },

    queryPremiumEntities(state, { payload }) {
      return {
        ...state,
        premium: payload?.items || [],
        executedCommand: null,
        premiumPagination: {
          ...state.premiumPagination,
          ...toPaginate(payload),
        },
      };
    },

    queryFavorites(state, { payload }) {
      return {
        ...state,
        favorites: payload,
        executedCommand: null,
      };
    },

    querySetFavorite(state, { payload }) {
      return {
        ...state,
        data: state.data.map((item) =>
          item.id === payload.id ? { ...item, isFavorite: !payload.isFavorite } : item,
        ),
        premium: state.premium.map((item) =>
          item.id === payload.id ? { ...item, isFavorite: !payload.isFavorite } : item,
        ),
        favorites: payload.isFavorite
          ? state.favorites.filter((item) => item.id !== payload.id)
          : state.favorites,
      };
    },

    markFavoritesChanged(state) {
      return {
        ...state,
        favoritesChanged: true,
      };
    },

    clearFavoritesChanged(state) {
      return {
        ...state,
        favoritesChanged: false,
      };
    },

    queryToggleActive(state, { payload }) {
      return {
        ...state,
        data: state.data.map((item) =>
          item.id === payload.id ? { ...item, isActive: payload.isActive } : item,
        ),
        premium: state.premium.map((item) =>
          item.id === payload.id ? { ...item, isActive: payload.isActive } : item,
        ),
        favorites: state.favorites.map((item) =>
          item.id === payload.id ? { ...item, isActive: payload.isActive } : item,
        ),
      };
    },

    queryEntityGroup(state, { payload }) {
      return {
        ...state,
        viewGroupsExecutedCommand: payload || false,
      };
    },

    entityById(state, { payload }) {
      return { ...state, selectedItem: payload };
    },

    querySaveEntity(state, { payload }) {
      return {
        ...state,
        executedCommand: DynamicEntityOperation.Add,
      };
    },

    querySearch(state, { payload }) {
      return {
        ...state,
        ...(_.isString(payload) && { searchCriteria: payload }),
        ...(_.isObject(payload) && {
          [`search${_.capitalize(payload.viewContext)}Criteria`]: payload.criteria,
        }),
      };
    },

    orderBy(state, { payload }) {
      return { ...state, orderBy: payload };
    },

    hideForms(state) {
      return {
        ...state,
        selectedItem: null,
      };
    },

    queryCleanup() {
      return initialState;
    },
  },
};

export default Model;
